import React, { useMemo } from 'react';
import { addDays, format, isSameDay, parseISO, startOfWeek } from 'date-fns';
import {
  APPOINTMENT_TYPE_LABELS,
  GRID_END_HOUR,
  GRID_START_HOUR,
  GRID_TOTAL_MINUTES,
  appointmentsForDay,
  blockStyle,
  leadDisplayName,
  minutesFromGridStart,
  slotIsoForDayAndMinutes,
  statusColor,
} from '../../lib/calendarUtils';

const SLOT_MINUTES = 30;
const SLOTS = [];
for (let h = GRID_START_HOUR; h < GRID_END_HOUR; h += 1) {
  SLOTS.push({ label: format(new Date(2000, 0, 1, h, 0), 'h a'), minutes: (h - GRID_START_HOUR) * 60 });
  SLOTS.push({ label: '', minutes: (h - GRID_START_HOUR) * 60 + 30 });
}

function CurrentTimeLine() {
  const now = new Date();
  const min = minutesFromGridStart(now);
  if (min < 0 || min > GRID_TOTAL_MINUTES) return null;
  return (
    <div
      className="absolute left-0 right-0 z-20 pointer-events-none border-t-2 border-red-500"
      style={{ top: `${(min / GRID_TOTAL_MINUTES) * 100}%` }}
    >
      <span className="absolute -left-1 -top-1.5 w-2 h-2 rounded-full bg-red-500" />
    </div>
  );
}

export default function CalendarTimeGridView({
  viewMode,
  focusDate,
  appointments,
  onSelectAppointment,
  onEmptySlotClick,
}) {
  const days = useMemo(() => {
    if (viewMode === 'day') return [focusDate];
    const start = startOfWeek(focusDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [viewMode, focusDate]);

  const colClass = viewMode === 'day' ? 'grid-cols-1' : 'grid-cols-7';

  return (
    <div className="flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-[36rem]">
      <div className={`grid ${colClass} border-b border-gray-200 bg-gray-50 shrink-0`}>
        {days.map((day) => (
          <div key={day.toISOString()} className="py-2 px-2 text-center border-r border-gray-100 last:border-r-0">
            <p className="text-xs font-semibold text-gray-500 uppercase">{format(day, 'EEE')}</p>
            <p className={`text-sm font-bold ${isSameDay(day, new Date()) ? 'text-[#00AAFF]' : 'text-gray-900'}`}>
              {format(day, 'd')}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-1 overflow-auto max-h-[32rem]">
        <div className="w-14 shrink-0 border-r border-gray-100 bg-gray-50">
          {SLOTS.filter((s) => s.label).map((slot) => (
            <div key={slot.minutes} className="h-12 text-[10px] text-gray-400 pr-1 text-right flex items-start justify-end pt-0.5">
              {slot.label}
            </div>
          ))}
        </div>

        <div className={`flex-1 grid ${colClass} min-w-0`}>
          {days.map((day) => {
            const dayAppts = appointmentsForDay(appointments, day);
            const showNow = isSameDay(day, new Date());

            return (
              <div key={day.toISOString()} className="relative border-r border-gray-100 last:border-r-0 min-h-[840px]">
                {SLOTS.map((slot) => (
                  <button
                    key={slot.minutes}
                    type="button"
                    className="absolute left-0 right-0 h-6 border-b border-gray-50 hover:bg-blue-50/30"
                    style={{ top: `${(slot.minutes / GRID_TOTAL_MINUTES) * 100}%`, height: `${(SLOT_MINUTES / GRID_TOTAL_MINUTES) * 100}%` }}
                    onClick={() => {
                      if (onEmptySlotClick) {
                        onEmptySlotClick(slotIsoForDayAndMinutes(day, slot.minutes));
                      }
                    }}
                    aria-label={`Schedule at ${format(day, 'MMM d')} slot`}
                  />
                ))}

                {showNow && <CurrentTimeLine />}

                {dayAppts.map((appt) => {
                  const style = blockStyle(appt);
                  const color = statusColor(appt.status);
                  const typeLabel = APPOINTMENT_TYPE_LABELS[appt.appointment_type] || appt.appointment_type;
                  return (
                    <button
                      key={appt.id}
                      type="button"
                      className="absolute left-1 right-1 z-10 rounded px-1.5 py-0.5 text-left text-white text-[11px] leading-tight overflow-hidden shadow-sm hover:opacity-95"
                      style={{ ...style, backgroundColor: color }}
                      onClick={() => onSelectAppointment(appt)}
                    >
                      <span className="font-semibold block truncate">{leadDisplayName(appt)}</span>
                      <span className="opacity-90 truncate block">{typeLabel}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
